package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderLeft
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.borderTop
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.FileFormat
import dev.lingvo.site.ds.ControlHeight
import dev.lingvo.site.ds.HIT_AREA_FLOOR_PX
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LIconButton
import dev.lingvo.site.ds.LMoonIcon
import dev.lingvo.site.ds.LSunIcon
import dev.lingvo.site.ds.LWordmarkGlyph
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.interactiveTransition
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.onInkPalette
import dev.lingvo.site.ds.pressScale
import dev.lingvo.site.ds.pressTransition
import dev.lingvo.site.ds.rememberColorModeWasChosen
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transition
import dev.lingvo.site.ds.transitionColors
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.Footer
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TagElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.Node
import org.w3c.dom.events.EventListener

/**
 * `5000` → `5,000`.
 *
 * Credit allowances are quoted on the pricing page and in the terms, and an unseparated `20000`
 * reads as a typo next to prose.
 *
 * **The grouping itself belongs to [formatCount]**, which the app pages already use for the same
 * job. This file held a second implementation of it, and the two had drifted: the copy here split
 * on length before reversing, so a negative value under a thousand came back as `-,100`. One name
 * survives for the marketing call sites that read as prose; the arithmetic is the design system's.
 */
fun formatThousands(value: Int): String = formatCount(value)

/**
 * Sticky, and translucent over whatever scrolls beneath it.
 *
 * The blur is what lets the bar stay legible while the glyph mosaic and the page's own type pass
 * under it — a solid bar would either hide the material or have to be opaque enough to look like a
 * separate slab bolted to the top of the page.
 *
 * `backdrop-filter` degrades cleanly: a browser without it simply gets the translucent colour, which
 * is why the alpha is high enough to carry the text on its own. The colour is mixed rather than a
 * flat rgba so it tracks the theme's own surface.
 */
val PublicHeaderStyle = CssStyle.base {
    val surface = colorMode.toSitePalette().surfaceBase
    Modifier
        .fillMaxWidth()
        .position(Position.Sticky)
        .top(0.px)
        .zIndex(200)
        .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .styleModifier {
            property("background-color", "color-mix(in srgb, $surface 82%, transparent)")
            property("backdrop-filter", "saturate(180%) blur(14px)")
            property("-webkit-backdrop-filter", "saturate(180%) blur(14px)")
        }
}

val PublicHeaderInnerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(Space.ContentMax)
            .height(64.px)
            .padding(leftRight = Space.LG)
            .gap(Space.SM)
            .styleModifier { property("margin", "0 auto") }
    }
    Breakpoint.MD {
        Modifier.padding(leftRight = Space.XXXL)
    }
}

private val PublicMenuDetailsElement = ElementBuilder.createBuilder<HTMLElement>("details")
private val PublicMenuSummaryElement = ElementBuilder.createBuilder<HTMLElement>("summary")

val PublicMobileMenuStyle = CssStyle {
    base { Modifier.position(Position.Relative) }
    Breakpoint.MD { Modifier.styleModifier { property("display", "none") } }
}

val PublicMobileMenuTriggerStyle = CssStyle {
    base {
        Modifier
            .height(HIT_AREA_FLOOR_PX.px)
            .padding(leftRight = Space.SM)
            .color(colorMode.toSitePalette().textPrimary)
            .borderRadius(Radius.Control)
            .fontSize(TypeScale.Small)
            .fontWeight(600)
            .outline(0.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("cursor", "pointer")
                property("list-style", "none")
            }
    }
    focusVisible {
        Modifier.outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

val PublicMobileMenuPanelStyle = CssStyle.base {
    Modifier
        .position(Position.Absolute)
        .top(100.percent)
        .right(0.px)
        .width(220.px)
        .padding(Space.SM)
        .backgroundColor(colorMode.toSitePalette().surfaceRaised)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
        .borderRadius(Radius.Card)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("max-height", "calc(100svh - 80px)")
            property("overflow-y", "auto")
            property("box-shadow", "0 12px 32px rgba(0, 0, 0, 0.14)")
        }
}

val PublicMobileMenuCurrentLinkStyle = CssStyle.base {
    Modifier
        .fontWeight(700)
        .color(colorMode.toSitePalette().textPrimary)
        .borderLeft(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
        .padding(left = Space.SM)
}

val PublicWordmarkStyle = CssStyle {
    base {
        Modifier
            .height(ControlHeight.MD)
            .color(colorMode.toSitePalette().textPrimary)
            .borderRadius(Radius.Control)
            .outline(0.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", Space.SM.toString())
                property("text-decoration", "none")
            }
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

val PublicWordmarkGlyphStyle = CssStyle.base {
    Modifier
        // The glyph box, not the link's own hit area (that's PublicWordmarkStyle, at
        // ControlHeight.MD) — a decorative square happening to share its number.
        .width(40.px)
        .height(40.px)
        .color(colorMode.toSitePalette().accent)
        .fontSize(TypeScale.Lead)
        .fontWeight(700)
        .lineHeight(1)
        .styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("flex-shrink", "0")
        }
}

val PublicTextLinkStyle = CssStyle {
    base {
        Modifier
            .color(colorMode.toSitePalette().textSecondary)
            // Padding, not a pseudo-element, because these links stack in the footer columns: real
            // spacing keeps adjacent 40px targets from overlapping, which a pseudo-element would not.
            .padding(topBottom = 10.px)
            .borderRadius(Radius.Control)
            .outline(0.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier {
                property("text-decoration", "none")
            }
            .transitionColors()
    }
    hover {
        Modifier.color(colorMode.toSitePalette().textPrimary)
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

/**
 * The primary action's focus ring, as a variable rather than a fixed token.
 *
 * Same fix, and the same measurement, as [dev.lingvo.site.ds.LButton]'s own ring: the page accent
 * is tuned for paper and measures 2.70:1 against an ink band in Day and 2.95:1 in Night, below the
 * 3:1 WCAG 2.4.11 asks of a focus indicator. The close band draws this control on ink, so it hands
 * the variable the accent measured for that ground. Every other call site sets nothing and keeps
 * the ring it always had.
 */
private val PublicActionRingVar by StyleVariable<CSSColorValue>(prefix = "lingvo")

val PublicPrimaryLinkStyle = CssStyle {
    base {
        Modifier
            // The named token, not a raw 48. `LButtonSize.LG` is the same height, which is what the
            // close band's copy of this control is drawn at — one action, one number, one source.
            .height(ControlHeight.LG)
            .padding(leftRight = Space.XL)
            .backgroundColor(colorMode.toSitePalette().accent)
            .color(colorMode.toSitePalette().accentContrast)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .borderRadius(Radius.Control)
            .fontSize(TypeScale.Body)
            .fontWeight(600)
            .outline(0.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("text-decoration", "none")
            }
            .interactiveTransition()
    }
    hover {
        Modifier.backgroundColor(colorMode.toSitePalette().accentHover)
    }
    cssRule(":active:not(:focus-visible)") {
        Modifier.pressScale().pressTransition()
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, PublicActionRingVar.value(colorMode.toSitePalette().accent))
            .styleModifier { property("outline-offset", "2px") }
    }
}

val PublicSecondaryLinkStyle = CssStyle {
    base {
        Modifier
            .height(ControlHeight.LG)
            .padding(leftRight = Space.XL)
            .backgroundColor(colorMode.toSitePalette().surface)
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Control)
            .fontSize(TypeScale.Body)
            .fontWeight(600)
            .outline(0.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("text-decoration", "none")
            }
            .interactiveTransition()
    }
    hover {
        Modifier.backgroundColor(colorMode.toSitePalette().surfaceRaised)
    }
    cssRule(":active:not(:focus-visible)") {
        Modifier.pressScale().pressTransition()
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

val PublicFooterStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .borderTop(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
}

val PublicFooterInnerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(Space.ContentMax)
            .padding(topBottom = Space.XL, leftRight = Space.LG)
            .gap(Space.XL)
            .styleModifier {
                property("margin", "0 auto")
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "flex-start")
            }
    }
    Breakpoint.MD {
        Modifier.padding(topBottom = Space.XXL, leftRight = Space.XXXL)
    }
}

val PublicFooterColumnsStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(Space.LG)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
            }
    }
    Breakpoint.MD {
        // The wordmark column is given the extra room so the three link columns stay even.
        Modifier.styleModifier { property("grid-template-columns", "1.6fr repeat(3, minmax(0, 1fr))") }
    }
}

/**
 * The legal links in the footer's bottom bar.
 *
 * Same 40px target as every other link, but carried by a pseudo-element rather than padding, so the
 * visible box stays the height of its text. With padding they read as two grey buttons sitting under
 * the colophon; the target has to be big, the link does not have to look big.
 *
 * Safe here and nowhere else on this page: these two sit side by side with 16px between them and
 * nothing above or below, so an area that grows vertically cannot overlap a neighbour's.
 */
val PublicLegalLinkStyle = CssStyle {
    base {
        Modifier
            .position(Position.Relative)
            .padding(topBottom = 0.px)
            .fontSize(TypeScale.Small)
    }
    cssRule("::after") {
        Modifier.styleModifier {
            property("content", "\"\"")
            property("position", "absolute")
            property("left", "0")
            property("right", "0")
            property("top", "50%")
            property("height", "${HIT_AREA_FLOOR_PX}px")
            property("transform", "translateY(-50%)")
        }
    }
}

val PublicFooterBottomStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(top = Space.LG)
            .gap(Space.SM)
            .borderTop(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "flex-start")
            }
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("flex-direction", "row")
            property("align-items", "center")
            property("justify-content", "space-between")
        }
    }
}

@Composable
fun PublicHeader() {
    Header(PublicHeaderStyle.toModifier().toAttrs()) {
        Row(PublicHeaderInnerStyle.toModifier(), verticalAlignment = Alignment.CenterVertically) {
            PublicWordmark()
            Spacer()
            Nav(
                attrs = Modifier
                    .displayIfAtLeast(Breakpoint.MD)
                    .toAttrs { attr("aria-label", tr("marketing.public_chrome.landing_page", "Landing page")) },
            ) {
                Row(Modifier.gap(Space.XL), verticalAlignment = Alignment.CenterVertically) {
                    // No in-page anchor here. "Workflow" sat in the same row as three real
                    // destinations and silently scrolled instead of navigating, which is the kind of
                    // thing that makes a nav feel untrustworthy without anyone being able to say why.
                    PublicTextLink("/docs", t("marketing.public_chrome.docs", "Docs"))
                    PublicTextLink("/pricing", t("marketing.public_chrome.pricing", "Pricing"))
                    PublicTextLink("/login", t("marketing.public_chrome.log_in", "Log in"))
                }
            }
            PublicMobileMenu()
            PublicThemeToggle(Modifier.displayIfAtLeast(Breakpoint.MD))
            PublicActionLink(
                path = "/signup",
                label = t("marketing.public_chrome.start_free", "Start free"),
                modifier = Modifier
                    // Not 32px: this is the smallest control on the page and it was below the
                    // hit-area floor the rest of the codebase honours. The style's own 48px default
                    // is too tall for a sticky bar, so it is set explicitly rather than unset.
                    .height(HIT_AREA_FLOOR_PX.px)
                    .padding(leftRight = Space.MD)
                    .fontSize(TypeScale.Small),
            )
        }
    }
}

/** Native disclosure keeps the three public destinations available before the long page body. */
@Composable
internal fun PublicMobileMenu() {
    TagElement(
        elementBuilder = PublicMenuDetailsElement,
        applyAttrs = PublicMobileMenuStyle.toModifier().toAttrs {
            onKeyDown { event ->
                if (event.key == "Escape") {
                    val details = event.currentTarget as HTMLElement
                    details.removeAttribute("open")
                    (details.querySelector("summary") as? HTMLElement)?.focus()
                    event.preventDefault()
                }
            }
            ref { details ->
                val outside = EventListener { event ->
                    if (!details.contains(event.target as? Node)) details.removeAttribute("open")
                }
                document.addEventListener("pointerdown", outside)
                onDispose { document.removeEventListener("pointerdown", outside) }
            }
        },
    ) {
        TagElement(
            elementBuilder = PublicMenuSummaryElement,
            applyAttrs = PublicMobileMenuTriggerStyle.toModifier().toAttrs(),
        ) { Text(t("marketing.public_chrome.menu", "Menu")) }
        Nav(PublicMobileMenuPanelStyle.toModifier().toAttrs {
            attr("aria-label", tr("marketing.public_chrome.mobile_navigation", "Mobile navigation"))
        }) {
            PublicMobileMenuLink("/docs", t("marketing.public_chrome.docs", "Docs"))
            PublicMobileMenuLink("/pricing", t("marketing.public_chrome.pricing", "Pricing"))
            PublicMobileMenuLink("/login", t("marketing.public_chrome.log_in", "Log in"))
            Row(
                Modifier.padding(leftRight = Space.SM).styleModifier { property("justify-content", "space-between") },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Span { Text(t("marketing.public_chrome.theme", "Theme")) }
                PublicThemeToggle()
            }
        }
    }
}

@Composable
private fun PublicMobileMenuLink(path: String, label: String) {
    val current = window.location.pathname == path
    A(
        href = path,
        attrs = PublicTextLinkStyle.toModifier()
            .then(if (current) PublicMobileMenuCurrentLinkStyle.toModifier() else Modifier)
            .toAttrs {
                if (current) attr("aria-current", "page")
            },
    ) { Text(label) }
}

@Composable
fun PublicWordmark(
    modifier: Modifier = Modifier,
    path: String = "/",
) {
    Link(
        path = path,
        modifier = PublicWordmarkStyle.toModifier().then(modifier),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    ) {
        // A bracket enclosing a rotating glyph, which is what DESIGN.md §"signature" specifies.
        // The static "[あ]" this replaces was the spec's own fallback shipped as the spec.
        LWordmarkGlyph(PublicWordmarkGlyphStyle.toModifier())
        Span(
            Modifier
                .displayFont()
                .fontSize(TypeScale.Lead)
                .fontWeight(700)
                .toAttrs(),
        ) {
            Text(t("marketing.public_chrome.lingvo", "Lingvo"))
        }
    }
}

@Composable
fun PublicThemeToggle(modifier: Modifier = Modifier) {
    var colorMode by ColorMode.currentState
    LIconButton(
        // The page's only icon-only control, so this string *is* its name to a screen reader — and
        // it was the one accessible name in the chrome the catalogue never saw, because the
        // extractor matches `label = "…"` and this one opens with an `if`.
        label = if (colorMode.isDark) {
            t("marketing.public_chrome.switch_to_light_theme", "Switch to light theme")
        } else {
            t("marketing.public_chrome.switch_to_dark_theme", "Switch to dark theme")
        },
        onClick = {
            // The only place this is recorded. A stored colour mode says nothing on its own —
            // AppEntry writes one for everybody on first composition — so the toggle is what
            // distinguishes a choice from a default. See COLOR_MODE_EXPLICIT_KEY.
            rememberColorModeWasChosen()
            colorMode = colorMode.opposite
        },
        modifier = modifier,
        variant = LButtonVariant.GHOST,
        size = LButtonSize.SM,
    ) {
        if (colorMode.isDark) LSunIcon() else LMoonIcon()
    }
}

@Composable
fun PublicTextLink(path: String, label: String, modifier: Modifier = Modifier) {
    Link(
        path = path,
        text = label,
        modifier = PublicTextLinkStyle.toModifier().then(modifier),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    )
}

/**
 * The marketing call to action, drawn on an anchor.
 *
 * [onInk] draws the focus ring for a `BandGround.Ink` section rather than for paper; leave it false
 * everywhere else. The fill and the label are unchanged either way — `accentContrast` on `accent`
 * clears 6.93:1 in Day — so this is a ring fix, exactly as it is on [dev.lingvo.site.ds.LButton].
 */
@Composable
fun PublicActionLink(
    path: String,
    label: String,
    modifier: Modifier = Modifier,
    primary: Boolean = true,
    onInk: Boolean = false,
) {
    val style = if (primary) PublicPrimaryLinkStyle else PublicSecondaryLinkStyle
    // Read in the composition, not inside `thenIf` — that lambda is not a composable scope, and
    // `ColorMode.current` is a composable read.
    val inkRing = ColorMode.current.onInkPalette().accent
    Link(
        path = path,
        text = label,
        modifier = style.toModifier()
            .thenIf(onInk) { Modifier.setVariable(PublicActionRingVar, inkRing) }
            .then(modifier),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    )
}

@Composable
fun PublicFooter() {
    val palette = ColorMode.current.toSitePalette()
    Footer(PublicFooterStyle.toModifier().toAttrs()) {
        Div(PublicFooterInnerStyle.toModifier().toAttrs()) {
            Nav(
                attrs = PublicFooterColumnsStyle.toModifier().toAttrs {
                    attr("aria-label", tr("marketing.public_chrome.footer", "Footer"))
                },
            ) {
                // The wordmark alone. The blurb that used to sit here said "priced by seat instead
                // of by word" — the same sentence as the H1, the close above it, and the colophon
                // below it. Four times on one page is not emphasis, it is a stutter.
                Column(Modifier.gap(Space.MD)) {
                    PublicWordmark()
                }
                // Both of this column's first two links used to point at ids that do not exist.
                // `/#proof` named a section no file in `site/src` has ever carried, and `/#formats`
                // was one character short of `formats-title` — so "The editor" and "Formats" both
                // reloaded the landing page and left the reader at the top of it, which is the
                // failure the comment above the header nav describes and the reason that nav has no
                // anchors left. "The editor" is gone until there is an editor surface to point at;
                // "Formats" now goes to `/docs/formats`, which is a real page that lists every
                // parser rather than a band that shows four.
                FooterColumn(
                    t("marketing.public_chrome.product", "Product"),
                    listOf(
                        "/docs/formats" to t("marketing.public_chrome.formats", "Formats"),
                        "/pricing" to t("marketing.public_chrome.pricing", "Pricing"),
                    ),
                )
                FooterColumn(
                    t("marketing.public_chrome.developers", "Developers"),
                    listOf(
                        "/docs" to t("marketing.public_chrome.documentation", "Documentation"),
                        "/agents" to t("marketing.public_chrome.coding_agents", "Coding agents"),
                        "/docs#install" to t("marketing.public_chrome.install_the_cli", "Install the CLI"),
                        "/docs#ci" to t("marketing.public_chrome.use_it_in_ci", "Use it in CI"),
                    ),
                )
                FooterColumn(
                    t("marketing.public_chrome.account", "Account"),
                    listOf(
                        "/login" to t("marketing.public_chrome.log_in", "Log in"),
                        "/signup" to t("marketing.public_chrome.start_free", "Start free"),
                    ),
                )
            }
            Div(PublicFooterBottomStyle.toModifier().toAttrs()) {
                Span(
                    Modifier
                        .monoFont()
                        .color(palette.textSecondary)
                        .fontSize(TypeScale.Small)
                        .toAttrs(),
                ) {
                    // The count comes from the enum the parsers are registered in, so the footer
                    // cannot go on claiming eleven formats on the day the twelfth ships. It was a
                    // hardcoded 11 one line above a claim about never miscounting anything.
                    Text(
                        tf(
                            "marketing.public_chrome.formats_review_export",
                            "lingvo · {count} formats · draft, review, export",
                        "count" to FileFormat.entries.size.toString()),
                    )
                }
                Row(
                    Modifier.gap(Space.LG).styleModifier { property("flex-wrap", "wrap") },
                ) {
                    PublicTextLink("/privacy", t("marketing.public_chrome.privacy", "Privacy"), PublicLegalLinkStyle.toModifier())
                    PublicTextLink("/terms", t("marketing.public_chrome.terms", "Terms"), PublicLegalLinkStyle.toModifier())
                    // Linked, not merely hosted: a payment processor verifying a domain checks that
                    // the site points at its own refund policy, and a human does the checking.
                    PublicTextLink("/refunds", t("marketing.public_chrome.refunds", "Refunds"), PublicLegalLinkStyle.toModifier())
                }
            }
        }
    }
}

@Composable
private fun FooterColumn(heading: String, links: List<Pair<String, String>>) {
    // No gap between the links. Each already carries 10px of padding above and below for its hit
    // area, so a gap on top of that stacked three deep is what made this footer twice the height it
    // needed. Adjacent 40px targets that touch are correct; only overlapping ones are a problem.
    Column(Modifier.gap(Space.XXS)) {
        Span(
            Modifier
                .color(ColorMode.current.toSitePalette().textPrimary)
                .fontSize(TypeScale.Micro)
                .fontWeight(600)
                .styleModifier {
                    property("text-transform", "uppercase")
                    property("letter-spacing", TypeScale.MicroTracking.toString())
                }
                .toAttrs(),
        ) { Text(heading) }
        links.forEach { (path, label) ->
            Link(
                path = path,
                text = label,
                modifier = PublicTextLinkStyle.toModifier().fontSize(TypeScale.Small),
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            )
        }
    }
}
